














































































































































































import { Component, Mixins, Ref, Watch } from "vue-property-decorator";
import MediaSelect from "@/components/MediaSelect.vue";
import TimeSlotPicker, { SlotObject } from "@/components/TimeSlotPicker.vue";
import DatePicker, {
  DATE_FORMAT,
  DatePickerData,
  DateString,
} from "@/components/DatePicker.vue";
import SelectOption from "@/interfaces/SelectOption";
import VMultiSwitcher from "@/components/VMultiSwitcher.vue";
import VSwitchButton from "@/components/VSwitchButton.vue";
import CustomChart, { CustomChartItem } from "@/components/CustomChart.vue";
import VCheckButton from "@/components/VCheckButton.vue";
import ComparisonGroupSelect from "@/components/ComparisonGroupSelect.vue";
import InvestorsMixins from "@/mixins/http/Investor";
import NumberFormatter from "@/services/NumberFormatter";
import {
  Company,
  ComparisonGroup,
  Media,
  Target,
  User,
  AdcontrolProfile,
} from "@/models";
import ComparatorTable, {
  ComparatorTableFilter,
} from "@/components/Investor/ComparatorTable.vue";
import ComparatorChart, {
  ComparatorChartFilter,
} from "@/components/Investor/ComparatorChart.vue";
import { Getter } from "vuex-class";
import MediaValue from "@/interfaces/MediaValue";
import Entry from "@/models/Entry";
import { FEATURE_AUDITEL } from "@/constants";
import GrpTargetSelect from "@/components/GrpTargetSelect.vue";
import MultiLevelSelect from "@/components/MultiLevelSelect.vue";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import Disclaimer from "@/components/GrpTargets/Disclaimer.vue";
import QueryParser from "@/services/QueryParser";
import QueryHelper from "@/services/QueryHelper";
import Compressor from "@/services/Compressor";
import BenchmarkModal from "@/components/Investor/BenchmarkModal.vue";
import VModal from "@/components/VModal.vue";
import Taxonomy from "@/interfaces/Taxonomy";
import {
  BenchmarkGranularity,
  BenchmarkTaxonomy,
} from "@/models/interfaces/Benchmark";
import PageGuide, { GuidePaths } from "@/components/PageGuide.vue";

const requestTag = "comparator-benchmark";

interface DateRange {
  from: DateString;
  to: DateString;
}

interface InvestorComparatorFilter {
  group: ComparisonGroup | null;
  unitOfMeasure: string;
  media: MediaValue;
  date: DateRange;
  timeSlots: SlotObject[];
  granularity: string;
  showBenchmark: boolean;
  benchmarkTaxonomy: BenchmarkTaxonomy;
  benchmarkGranularity: BenchmarkGranularity;
}

@Component({
  components: {
    BenchmarkModal,
    Disclaimer,
    MediaSelect,
    TimeSlotPicker,
    DatePicker,
    VMultiSwitcher,
    VSwitchButton,
    VCheckButton,
    ComparisonGroupSelect,
    CustomChart,
    ComparatorChart,
    ComparatorTable,
    GrpTargetSelect,
    MultiLevelSelect,
    PageGuide,
  },
})
export default class InvestorComparator extends Mixins(InvestorsMixins) {
  @Getter("lastDate") readonly lastDate!: string;
  @Getter("userMedia") readonly userMedia!: MediaValue;
  @Getter("user") readonly user!: User;
  @Getter("profile") readonly profile!: AdcontrolProfile;
  @Getter("taxonomies") readonly taxonomies!: Array<Taxonomy>;
  @Getter readonly parseDateValue!: (
    date: string,
    format: string,
    defaultOffset?: number,
    // eslint-disable-next-line
    defaultUnit?: any
  ) => string;
  @Getter readonly parseAndCleanMediaValue!: (
    radio: string | boolean | undefined,
    tv: string | boolean | undefined
  ) => MediaValue;

  @Ref() readonly benchmarkModal!: VModal;

  currentGrpTarget: Target | null = null;

  filter: InvestorComparatorFilter = {
    group: null,
    timeSlots: [],
    date: {
      from: this.$moment(this.lastDate)
        .subtract(3, "months")
        .format(DATE_FORMAT) as DateString,
      to: this.$moment(this.lastDate).format(DATE_FORMAT) as DateString,
    },
    granularity: "",
    unitOfMeasure: "spots",
    media: {
      radio: true,
      tv: true,
    },
    showBenchmark: false,
    benchmarkTaxonomy: {
      sector: [],
      category: [],
      class: [],
    },
    benchmarkGranularity: "companies",
  };

  valueInSeconds = false;

  chart = {
    settings: {
      stacked: true,
      percentage: false,
      type: "line",
    },
  };

  items = [];
  hiddenItems: Entry[] = [];

  @Watch("filter", { deep: true })
  async onFilterUpdated() {
    if (this.filter.media.radio) {
      if (
        this.filter.unitOfMeasure == "grp" ||
        this.filter.unitOfMeasure == "grp_over_spots" ||
        this.filter.unitOfMeasure == "audience"
      ) {
        this.filter.unitOfMeasure = "spots";
      }
    }
    const isEqual = QueryHelper.isSameQuery(
      this.$route.query,
      this.queryFilter
    );
    if (!isEqual) {
      this.$router.replace({ query: { ...this.queryFilter } });
    }
  }

  private granularityItems: Array<SelectOption> = [
    { id: "day", text: "Giorno" },
    { id: "sweek", text: "Settimana" },
    { id: "month", text: "Mese" },
  ];

  private chartTypeItems: Array<SelectOption> = [
    { id: "bar", text: "Barre" },
    { id: "line", text: "Linea" },
    { id: "pie", text: "Torta", disabled: true },
  ];

  guidePath = GuidePaths.INVESTOR_COMPARATOR;

  get queryFilter() {
    let slots = "";
    if (this.filter?.timeSlots.length > 0) {
      slots = this.filter.timeSlots
        .map((slot) => {
          return `${slot.from}|${slot.to}`;
        })
        .join(",");
    }

    return Object.assign(
      {},
      {
        from: this.filter.date.from as string,
        to: this.filter.date.to as string,
        radio: this.filter.media.radio ? "1" : "0",
        tv: this.filter.media.tv ? "1" : "0",
        unit: this.filter.unitOfMeasure,
        granularity: this.filter.granularity,
        showBenchmark: this.filter.showBenchmark ? "1" : "0",
        benchmarkSector: Compressor.compress(
          this.filter.benchmarkTaxonomy.sector as string[]
        ),
        benchmarkCategory: Compressor.compress(
          this.filter.benchmarkTaxonomy.category as string[]
        ),
        benchmarkClass: Compressor.compress(
          this.filter.benchmarkTaxonomy.class as string[]
        ),
        benchmarkGranularity: this.filter.benchmarkGranularity,
      },
      slots !== "" ? { slots } : {}
    );
  }

  get hasAuditel(): boolean {
    return this.user.hasFeature(FEATURE_AUDITEL);
  }

  get isVodOnly(): boolean {
    return this.profile?.mediaSourceKind === "msk_vod";
  }

  get disabled(): boolean {
    return this.filter.group == undefined;
  }

  get chartLabels(): string[] {
    const start = this.$moment(this.filter.date.from);
    const end = this.$moment(this.filter.date.to).add(1, "days");

    const labels = new Set();

    switch (this.filter.granularity) {
      case "day":
        for (let date = start; date.isBefore(end); date.add(1, "days")) {
          const label = date.format("D MMM");
          labels.add(label);
        }

        break;

      case "sweek":
        for (let date = start; date.isBefore(end); date.add(1, "days")) {
          const label = date.clone().startOf("week").format("D MMM");
          labels.add(label);
        }

        break;

      case "month":
        for (let date = start; date.isBefore(end); date.add(1, "days")) {
          const label = date.format("MMM YYYY");
          labels.add(label.charAt(0).toUpperCase() + label.slice(1));
        }

        break;
    }

    return [...labels] as string[];
  }

  get comparatorChartFilter(): ComparatorChartFilter {
    const group = this.filter.group
      ? (this.filter.group as unknown as ComparisonGroup).id
      : null;
    let grpTarget = undefined;
    if (this.currentGrpTarget?.id) {
      grpTarget = {
        grpTarget: this.currentGrpTarget.id,
      };
    }
    return {
      date: this.filter.date as any,
      granularity: this.filter.granularity,
      media: this.filter.media,
      group: group,
      timeSlots: this.filter.timeSlots,
      ...grpTarget,
      showBenchmark: this.filter.showBenchmark,
      benchmarkTaxonomy: this.filter.benchmarkTaxonomy,
      benchmarkGranularity: this.filter.benchmarkGranularity,
    };
  }

  get comparatorTableFilter(): ComparatorTableFilter {
    let grpTarget = undefined;
    if (this.currentGrpTarget?.id) {
      grpTarget = {
        grpTarget: this.currentGrpTarget.id,
      };
    }

    return {
      group: this.filter.group,
      timeSlots: this.filter.timeSlots,
      date: this.filter.date as any,
      media: this.filter.media,
      ...grpTarget,
      benchmarkGranularity: this.filter.benchmarkGranularity,
      benchmarkTaxonomy: this.filter.benchmarkTaxonomy,
    };
  }

  get unitOfMeasureName() {
    switch (this.filter.unitOfMeasure) {
      case "seconds":
        return "Secondi";
      case "spots":
        return "N° Spot";
      case "grp":
        return "GRP";
      case "grp_over_spots":
        return "GRP / Spot";
      case "audience":
        return "Contatti";
      default:
        return "Dati";
    }
  }

  get unitSelectorOptions(): Array<MultiLevelObject> {
    const alwaysOn = [
      { id: "seconds", text: "Secondi" },
      { id: "spots", text: "Spot" },
    ];

    if (this.hasAuditel && !this.filter.media.radio) {
      return [
        ...alwaysOn,

        { id: "grp", text: "GRP" },
        { id: "grp_over_spots", text: "GRP / Spot" },
        { id: "audience", text: "Contatti" },
      ];
    } else {
      return alwaysOn;
    }
  }

  async onTargetSelected(target: Target) {
    this.currentGrpTarget = target;
  }

  parseParamsOrDefault(param: string | undefined, defaultValue: boolean) {
    return param !== undefined ? (param == "1" ? true : false) : defaultValue;
  }

  buildFilterFromQuery(): InvestorComparatorFilter {
    const query = this.$route.query;
    const { radio, tv } = query;

    const from = this.parseDateValue(
      query.from as string,
      DATE_FORMAT,
      6,
      "days"
    );
    const to = this.parseDateValue(query.to as string, DATE_FORMAT);

    const media = this.parseAndCleanMediaValue(
      QueryParser.queryToBoolean(radio),
      QueryParser.queryToBoolean(tv)
    );
    const granularity = query.granularity
      ? (query.granularity as string)
      : this.getDefaultGranularity(from, to);
    const slots: string = query.slots ? (query.slots as string) : "";
    let timeSlots: SlotObject[] = [];
    if (slots !== "") {
      timeSlots = slots.split(",").map((slot) => {
        const parts = slot.split("|");
        return {
          from: parts[0],
          to: parts[1],
        };
      });
    }

    const showBenchmark = QueryParser.queryToBoolean(
      query.showBenchmark,
      false
    ) as boolean;
    const benchmarkTaxonomy =
      query.benchmarkSector || query.benchmarkCategory || query.benchmarkClass
        ? {
            sector:
              (Compressor.decompress(
                query.benchmarkSector as string
              ) as string[]) || undefined,
            category:
              (Compressor.decompress(
                query.benchmarkCategory as string
              ) as string[]) || undefined,
            class:
              (Compressor.decompress(
                query.benchmarkClass as string
              ) as string[]) || undefined,
          }
        : this.getAllBenchmarkTaxonomies();
    const benchmarkGranularity =
      (query.benchmarkGranularity as BenchmarkGranularity) || "companies";

    return {
      group: this.filter.group,
      unitOfMeasure: (query.unit as string) ?? "spots",
      media,
      granularity,
      timeSlots,
      date: {
        from,
        to,
      },
      showBenchmark,
      benchmarkTaxonomy,
      benchmarkGranularity,
    };
  }

  created() {
    this.filter = this.buildFilterFromQuery();
  }

  getDefaultGranularity(
    from = this.filter.date.from,
    to = this.filter.date.to
  ) {
    const start = this.$moment(from);
    const end = this.$moment(to);
    const days = end.diff(start, "days") + 1;

    if (days > 365) {
      return "month";
    } else if (days > 31) {
      return "sweek";
    } else {
      return "day";
    }
  }

  getRandomElements(min: number, max: number): number[] {
    if (this.chartLabels.length == 0) {
      return [];
    }

    const data: number[] = [];

    this.chartLabels.forEach(() => {
      const random = NumberFormatter.random(min, max);
      data.push(random);
    });

    return data;
  }

  getSampleItems(): CustomChartItem[] {
    let min, max;
    if (this.valueInSeconds) {
      min = 25000;
      max = 55000;
    } else {
      min = 10;
      max = 2000;
    }

    return [
      {
        label: "AMERICAN EXPRESS",
        data: this.getRandomElements(min, max),
      },
      {
        label: "UNIPOLSAI ASSICURAZIONI",
        data: this.getRandomElements(min, max),
      },
      {
        label: "COMPASS PRESTITI",
        data: this.getRandomElements(min, max),
      },
      {
        label: "CATTOLICA ASSICURAZIONI",
        data: this.getRandomElements(min, max),
      },
    ];
  }

  onDateSelected(data: DatePickerData) {
    if (data.range) {
      this.filter.date = data.range;
    }
  }

  onChartItemHidden(item: Entry) {
    this.hiddenItems.push(item);
  }

  onChartItemVisible(item: Entry) {
    const index = this.hiddenItems.indexOf(item);

    if (index != -1) {
      this.hiddenItems.splice(index, 1);
    }
  }

  onBenchmarkSettings() {
    this.benchmarkModal.show();
  }

  async onBenchmarkModalClose({
    taxonomy,
    granularity,
  }: {
    taxonomy: BenchmarkTaxonomy;
    granularity: BenchmarkGranularity;
  }) {
    this.filter.benchmarkTaxonomy = taxonomy;
    this.filter.benchmarkGranularity = granularity;
  }

  private getAllBenchmarkTaxonomies() {
    if (this.taxonomies == undefined) {
      return {
        sector: [],
        category: [],
        class: [],
      };
    }

    return {
      sector: this.taxonomies.map((sector) => sector.id),
      category: [],
      class: [],
    };
  }
}
